import React, { useMemo } from 'react';

import Typography from '../../../../library/typography/typography';

import { useAemContent } from '../../../../shared/hooks/hooks';

import { interpolateCmsString } from '../../../../shared/utils/cms/cms';

import { Maybe, ProductData } from '../../../../shared/types/graphql-types';
import {
  AEM_CONTENT_PAGE_IDS,
  AEM_TREADWELL_AI_CONTENT_IDS
} from '../../../../shared/constants/cms';

interface IntroductionMessageProps {
  isComparingProducts?: boolean;
  onMessageSet: (msg: string) => void;
  products?: Array<Maybe<ProductData>>;
}

function IntroductionMessage(props: IntroductionMessageProps) {
  const { isComparingProducts = false, onMessageSet, products } = props;
  const { recommendationData, tdsData } = products?.[0] || {};
  const { rank, topRecommendedProductData } = recommendationData || {};
  const { code: topRecommendedProductCode } =
    topRecommendedProductData?.frontProduct || {};
  const highestRankedProduct = useMemo(
    () =>
      products?.reduce((highestRankedProduct, currentProduct) => {
        if (highestRankedProduct?.recommendationData?.rank === 1) {
          return highestRankedProduct;
        }

        return Number(currentProduct?.recommendationData?.rank) <
          Number(highestRankedProduct?.recommendationData?.rank)
          ? currentProduct
          : highestRankedProduct;
      }, products?.[0]),
    [products]
  );
  const { aemContent } = useAemContent(AEM_CONTENT_PAGE_IDS.treadwellPdp);

  const topRecommendedProductMsg =
    aemContent?.[
      AEM_TREADWELL_AI_CONTENT_IDS.treadwellAiCurrentTopRecommendedProduct
    ]?.html;
  const notCurrentTopRecommendedProductMsg =
    aemContent?.[
      AEM_TREADWELL_AI_CONTENT_IDS.treadwellAiNotCurrentTopRecommendedProduct
    ]?.html;
  const defaultDataSummaryMsg =
    aemContent?.[AEM_TREADWELL_AI_CONTENT_IDS.treadwellAiDefaultDataSummary]
      ?.html;
  const defaultNoDataSummaryMsg =
    aemContent?.[AEM_TREADWELL_AI_CONTENT_IDS.treadwellAiDefaultNoDataSummary]
      ?.html;
  const plpCompareMsg = interpolateCmsString(
    {
      product: `${highestRankedProduct?.brand ?? ''} ${
        highestRankedProduct?.name ?? ''
      }`
    },
    aemContent?.[AEM_TREADWELL_AI_CONTENT_IDS.treadwellAiPlpCompare]?.html
  );

  if (isComparingProducts && highestRankedProduct) {
    const { brand, name } = highestRankedProduct;
    onMessageSet('compare to top recommended');

    return plpCompareMsg ? (
      <div
        dangerouslySetInnerHTML={{
          __html: plpCompareMsg
        }}
      />
    ) : (
      <Typography>
        Based on tire testing at Treadwell Research Park, the better tire for
        your vehicle, your driving area, and your driving style is the&nbsp;
        <Typography tag="span" weight="black">
          {brand} {name}.
        </Typography>
      </Typography>
    );
  } else if (rank === 1) {
    onMessageSet('great choice');

    return topRecommendedProductMsg ? (
      <div
        dangerouslySetInnerHTML={{
          __html: topRecommendedProductMsg
        }}
      />
    ) : (
      <Typography>
        Great choice,&nbsp;
        <Typography tag="span" weight="black">
          this is the best tire for you!
        </Typography>
      </Typography>
    );
  } else if (topRecommendedProductCode || (rank && rank !== 1)) {
    onMessageSet('compare to top recommended');

    return notCurrentTopRecommendedProductMsg ? (
      <div
        dangerouslySetInnerHTML={{
          __html: notCurrentTopRecommendedProductMsg
        }}
      />
    ) : (
      <Typography>
        See how this tire compares to the #1 Treadwell recommendation for your
        car, your driving area, and your driving style based on tire testing at
        Treadwell Research Park.
      </Typography>
    );
  } else if (tdsData) {
    onMessageSet('non treadwell');

    return defaultDataSummaryMsg ? (
      <div
        dangerouslySetInnerHTML={{
          __html: defaultDataSummaryMsg
        }}
      />
    ) : (
      <Typography>
        <Typography tag="span" weight="black">
          This tire has been tested at our Treadwell Research Park!&nbsp;
        </Typography>
        See how this compares to the others including the very best rated tire
        for your car, your driving style, and your location based on results
        from our Tire Testing Track.
      </Typography>
    );
  } else {
    onMessageSet('non treadwell');

    return defaultNoDataSummaryMsg ? (
      <div
        dangerouslySetInnerHTML={{
          __html: defaultNoDataSummaryMsg
        }}
      />
    ) : (
      <Typography weight="black">
        This is a great tire, but is it your best match?
      </Typography>
    );
  }
}

export default IntroductionMessage;
